import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Fab,
  Fade,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Typography,
  useTheme,
  Button,
  Select,
  MenuItem,
} from "@mui/material";
import { Box } from "@mui/system";
import { ClickAwayListener } from "@mui/material";

import {
  selectCell,
  selectCells,
  selectLocation,
  selectRegion,
  selectRegions,
  selectUsername,
  setCell,
  setLocation,
  setRegion,
} from "../features/session/sessionSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { ReactComponent as Close } from "../assets/svg/Close.svg";
import { ReactComponent as Add } from "../assets/svg/Plus.svg";
import { ReactComponent as LogOutSquare } from "../assets/svg/LogOutSquare.svg";
import { signOut } from "../aws";
import { makeStyles } from "@mui/styles";
import { cellData } from "../features/aircraft/state";
import { resetConnections } from "../features/aircraft/aircraftSlice";

const useStyles = makeStyles({
  select: {
    "&:before": {
      borderColor: "white",
    },
    "&:after": {
      borderColor: "white",
    },
    "&:not(.Mui-disabled):hover::before": {
      borderColor: "white",
    },
  },
  icon: {
    fill: "white",
  },
  root: {
    color: "white",
  },
});

export const Menu = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const theme = useTheme();
  const classes = useStyles();
  const fabRef = React.useRef<HTMLButtonElement | null>(null);

  const dispatch = useAppDispatch();
  const location = useAppSelector(selectLocation);
  const cell = useAppSelector(selectCell);
  const region = useAppSelector(selectRegion);
  const regions = useAppSelector(selectRegions);
  const username = useAppSelector(selectUsername);
  const cells = useAppSelector(selectCells);
  const [loading, setLoading] = useState(false);
  const handleLocationChange = (event: SelectChangeEvent) => {
    dispatch(
      setCell(
        cells?.find((cell) => cell.cellName === event.target.value) as any
      )
    );
  };

  const onClickAway = () => {
    if (menuOpen) {
      setMenuOpen(false);
    }
  };
  const onClickSignOut = async () => {
    try {
      setLoading(true);
      await signOut();
      dispatch(resetConnections());
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  const onClickFab = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuOpen(!menuOpen);
  };
  const handleChange = (event: SelectChangeEvent) => {
    event.stopPropagation();
    dispatch(
      setRegion(
        regions?.find(
          (region) => region.regionName === event.target.value
        ) as any
      )
    );
  };

  useEffect(() => {
    const handleTouch = (e: TouchEvent) => {
      e.preventDefault();
      setMenuOpen(!menuOpen);
    };

    const element = fabRef.current;
    element?.addEventListener("touchstart", handleTouch, { passive: false });

    return () => {
      element?.removeEventListener("touchstart", handleTouch);
    };
  }, [menuOpen]);

  return (
    <Box sx={{}}>
      <Fab
        ref={fabRef}
        sx={{
          position: "absolute",
          bottom: 16,
          left: 16,
          pointerEvents: "all",
          backgroundColor: theme.palette.background.paper,
        }}
        aria-label={"fab"}
        onClick={onClickFab}
      >
        {menuOpen ? (
          <Close stroke={theme.palette.text.primary} />
        ) : (
          <Add stroke={theme.palette.text.primary} />
        )}
      </Fab>
      <ClickAwayListener onClickAway={onClickAway}>
        <div>
          <Fade in={menuOpen}>
            <Paper
              sx={{
                pointerEvents: "all",
                marginBottom: 11,
                marginLeft: 2,
                width: 375,
                borderRadius: 2,
              }}
            >
              <Box p={3}>
                <Box
                  sx={{}}
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"space-between"}
                >
                  <Box sx={{}} display={"flex"}>
                    <Typography variant={"h6"} fontWeight={"600"}>
                      Locations
                    </Typography>
                  </Box>
                  <Box sx={{}} display={"flex"}>
                    <Select
                      className={classes.select}
                      onChange={handleChange}
                      value={region?.regionName || ""}
                      MenuProps={{
                        disablePortal: true,
                      }}
                      sx={{ width: 100, height: 36 }}
                      inputProps={{
                        classes: {
                          icon: classes.icon,
                          root: classes.root,
                        },
                      }}
                      color="secondary"
                    >
                      {regions.map((region) => {
                        return (
                          <MenuItem
                            key={region.regionName}
                            value={region.regionName}
                          >
                            {region.regionName}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </Box>
                </Box>
                <Box display={"flex"} mt={1}>
                  <FormControl sx={{ width: "100%", padding: 0 }}>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      name="radio-buttons-group"
                      onChange={handleLocationChange}
                      value={cell?.cellName}
                    >
                      {cells.map((cell) => {
                        return (
                          <FormControlLabel
                            key={cell.cellName}
                            value={cell.cellName}
                            labelPlacement="start"
                            componentsProps={{}}
                            style={{ marginLeft: 0 }}
                            control={
                              <Radio
                                sx={{
                                  display: "flex",
                                  color: theme.palette.text.primary,
                                  "&, &.Mui-checked": {
                                    color: theme.palette.text.primary,
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography
                                variant={"body1"}
                                style={{
                                  width: "100%",
                                  display: "flex",
                                }}
                              >
                                {cellData?.[cell?.cellName]?.name ||
                                  cell.cellName}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>
              <Box bgcolor={"white"} width={"100%"} height={2}></Box>
              <Box
                pt={3}
                pb={2}
                px={3}
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
              >
                <Box display={"flex"} flexDirection={"column"}>
                  <Box>
                    <Typography
                      variant={"subtitle1"}
                      fontWeight={"600"}
                      sx={{ textJustify: "start", textAlign: "start" }}
                    >
                      Log Out
                    </Typography>
                    <Typography
                      variant={"subtitle1"}
                      fontWeight={"600"}
                      sx={{ textJustify: "start", textAlign: "start" }}
                    >
                      {username}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  onClick={onClickSignOut}
                  // disabled={props.loading || props.disabled}
                  sx={{
                    height: 64,
                    width: 64,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={22} sx={{ color: "white" }} />
                  ) : (
                    <LogOutSquare stroke={theme.palette.text.primary} />
                  )}
                </Button>
              </Box>
            </Paper>
          </Fade>
        </div>
      </ClickAwayListener>
    </Box>
  );
};
